import { useEffect, useRef } from 'react';
import { useReducedMotion } from 'framer-motion';
import Lenis from 'lenis';
import 'lenis/dist/lenis.css';

export const useDashboardMotion = (pathname) => {
  const wrapperRef = useRef(null);
  const contentRef = useRef(null);
  const scrollRef = useRef(null);
  const previousPathRef = useRef(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const content = contentRef.current;
    if (!wrapper || !content || reduced) return undefined;

    // Only the page scrolls smoothly; menus, editors and touch stay native.
    const scroll = new Lenis({
      wrapper,
      content,
      eventsTarget: wrapper,
      autoRaf: true,
      duration: 0.85,
      smoothWheel: true,
      syncTouch: false,
      allowNestedScroll: true,
      prevent: (node) => Boolean(node.closest?.('textarea, input, [contenteditable]:not([contenteditable="false"]), [role="dialog"], [role="alertdialog"], [role="listbox"], [role="menu"], [data-lenis-prevent]')),
    });
    scrollRef.current = scroll;
    return () => {
      scroll.destroy();
      scrollRef.current = null;
    };
  }, [reduced]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;
    if (previousPathRef.current === pathname) return undefined;
    previousPathRef.current = pathname;
    // Do not remount route children or reset forms for a visual transition.
    if (scrollRef.current) scrollRef.current.scrollTo(0, { immediate: true, force: true });
    else wrapper.scrollTop = 0;
    const animation = !reduced && contentRef.current?.animate?.(
      [
        { opacity: 0.7 },
        { opacity: 1 },
      ],
      { duration: 240, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
    );
    return () => animation?.cancel?.();
  }, [pathname, reduced]);

  return { wrapperRef, contentRef };
};
